import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight, Check, ShieldCheck, Users, BarChart3, Sparkles, KeyRound,
  Settings2, Lock, Layers, Workflow, CreditCard, Building2, Headphones,
  Globe, FileSignature, UserPlus, Zap, Database,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { alternateLinkList } from "@/lib/i18n/locale";
import networking from "@/assets/networking.jpg";
import heroCard from "@/assets/hero-card.webp";
import aiAbstract from "@/assets/ai-abstract.jpg";

export const Route = createFileRoute("/vi/teams")({
  head: () => ({
    meta: [
      { title: "banana.cards cho Đội nhóm — Thẻ kỹ thuật số & AI CRM ở quy mô doanh nghiệp" },
      { name: "description", content: "Trang bị cho mỗi nhân viên một thẻ kỹ thuật số có thương hiệu, ghi nhận mọi khách hàng tiềm năng vào một CRM, và triển khai NFC, SSO và phân tích trên toàn tổ chức." },
      { property: "og:title", content: "banana.cards cho Đội nhóm" },
      { property: "og:description", content: "Một nền tảng cho thẻ kỹ thuật số, ghi nhận khách hàng, AI CRM, hợp đồng và tự động hóa — xây dựng cho đội bán hàng, agency và doanh nghiệp." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/teams", "vi"),
  }),
  component: TeamsPage,
});

const HERO_METRICS = [
  { v: "200k+", l: "chuyên gia đang dùng banana" },
  { v: "65×", l: "theo dõi nhanh hơn so với thẻ giấy" },
  { v: "92%", l: "đội tiết kiệm 4+ giờ/tuần" },
  { v: "SOC 2", l: "Type II — sẵn sàng cho doanh nghiệp" },
];

const TRUSTED_BY = ["Allianz", "JLL", "Northbeam", "Verta", "Pelo", "Linear", "HubSpot", "Notion"];

const PILLARS = [
  {
    icon: CreditCard,
    title: "Thẻ kỹ thuật số có thương hiệu cho mỗi chỗ ngồi",
    desc: "Triển khai danh tính nhất quán trên toàn công ty — logo, màu sắc, liên kết và chữ ký gắn với thương hiệu. Cập nhật một lần, mọi thẻ đều cập nhật.",
    points: ["Mẫu khóa thương hiệu", "Nhập hàng loạt qua CSV / HRIS", "Trình tạo chữ ký email"],
  },
  {
    icon: Users,
    title: "Mọi khách hàng tiềm năng vào một CRM chung",
    desc: "Khi đồng nghiệp chạm, quét hoặc chia sẻ, liên hệ sẽ về một workspace chung — được làm giàu, dedupe và phân về pipeline đúng.",
    points: ["Pipeline chung & riêng", "Làm giàu khách hàng bằng AI", "Phân công luân phiên"],
  },
  {
    icon: ShieldCheck,
    title: "Bảo mật & kiểm soát dành cho IT",
    desc: "SSO, cấp phát SCIM, phân quyền theo vai trò, nhật ký kiểm toán và quyền chi tiết — dữ liệu vẫn là của bạn, ngay cả khi người dùng nghỉ việc.",
    points: ["SAML SSO + SCIM", "Phân quyền RBAC chi tiết", "GDPR & SOC 2 Type II"],
  },
];

const DEEP_DIVES = [
  {
    eyebrow: "Tuyển dụng",
    title: "Trang bị cho cả đội",
    italic: "trong một buổi chiều.",
    desc: "Mời qua CSV, Google Workspace hoặc HRIS của bạn. Banana tự động tạo thẻ có thương hiệu cho mỗi nhân viên, gửi sản phẩm NFC đến bàn làm việc và đồng bộ hồ sơ khi thay đổi vai trò.",
    image: networking,
    points: [
      "Mời hàng loạt & tự động tạo thẻ",
      "Đồng bộ Google Workspace, Okta, BambooHR",
      "Off-board chỉ trong một cú nhấp — giữ dữ liệu, vô hiệu chỗ ngồi",
      "Tập trung hóa giao hàng NFC & đặt lại",
    ],
  },
  {
    eyebrow: "Ghi nhận khách hàng",
    title: "Biến mỗi cuộc gặp",
    italic: "thành pipeline.",
    desc: "Chạm để chia sẻ, quét thẻ giấy, chuyển tiếp email hoặc gửi ghi âm — AI của banana ghi nhận liên hệ, làm giàu bằng dữ liệu doanh nghiệp và soạn bản theo dõi đúng giọng văn của bạn.",
    image: aiAbstract,
    reverse: true,
    points: [
      "Quét thẻ AI & phân tích email",
      "Làm giàu firmographic + xã hội",
      "Tự động soạn theo dõi bằng giọng của bạn",
      "Định tuyến thông minh đến đúng nhân viên",
    ],
  },
  {
    eyebrow: "Phân tích",
    title: "Thấy điều gì đang hiệu quả —",
    italic: "trên mỗi nhân viên.",
    desc: "Bảng điều khiển thời gian thực cho lần chạm, ghi nhận, cuộc họp đã đặt và doanh thu ảnh hưởng. Lọc theo đội, khu vực hoặc chiến dịch để xem ai đang tạo pipeline và nơi nào cần đẩy mạnh.",
    image: heroCard,
    points: [
      "Bảng xếp hạng theo nhân viên & đội",
      "Phễu chuyển đổi & xem cohort",
      "Phân bổ nhận biết UTM",
      "Xuất ra công cụ BI hoặc Sheets",
    ],
  },
];

const PLATFORM_FEATURES = [
  { icon: CreditCard, title: "Thẻ NFC & phụ kiện đeo", desc: "Kim loại, đen hoặc tùy chỉnh — một chạm chia sẻ toàn bộ hồ sơ." },
  { icon: Layers, title: "Nhiều thẻ cho mỗi người dùng", desc: "Bán hàng, tuyển dụng hoặc danh tính cho sự kiện cụ thể." },
  { icon: FileSignature, title: "Hợp đồng & ký điện tử", desc: "Gửi và ký thỏa thuận mà không rời khỏi liên hệ." },
  { icon: Workflow, title: "Pipeline & nhiệm vụ", desc: "Đẩy giao dịch tiến lên với quy trình chia sẻ." },
  { icon: Zap, title: "Tự động hóa", desc: "Trigger giữa CRM, email và Slack." },
  { icon: BarChart3, title: "Phân tích & xuất dữ liệu", desc: "Dashboard thời gian thực, BigQuery & Sheets." },
  { icon: Database, title: "API mở & webhook", desc: "Kết nối banana vào hệ thống hiện có." },
  { icon: Globe, title: "Lưu trữ đa khu vực", desc: "Hỗ trợ lưu trữ dữ liệu tại EU & US." },
];

const SECURITY = [
  { icon: KeyRound, title: "SAML SSO", desc: "Okta, Google, Azure AD và bất kỳ IdP SAML 2.0 nào." },
  { icon: UserPlus, title: "Cấp phát SCIM", desc: "Tự động đồng bộ người dùng & nhóm." },
  { icon: Lock, title: "Phân quyền theo vai trò", desc: "Owner, Admin, Manager, Member, Guest." },
  { icon: ShieldCheck, title: "SOC 2 Type II + GDPR", desc: "Được kiểm toán độc lập, tùy chọn lưu trữ tại EU." },
  { icon: Settings2, title: "Nhật ký kiểm toán", desc: "Mọi hành động được xuất sang SIEM của bạn." },
  { icon: Building2, title: "DPA & MSA tùy chỉnh", desc: "Giấy tờ pháp lý sẵn sàng trên gói doanh nghiệp." },
];

const USE_CASES = [
  { title: "Đội bán hàng", desc: "Ghi nhận khách hàng tại sự kiện, tự động làm giàu và định tuyến đến đúng nhân viên trước khi đối thủ kịp chớp mắt.", href: "/vi/solutions/sales" },
  { title: "Agency & tư vấn", desc: "Cho mỗi nhà tư vấn một danh tính chuyên nghiệp và sổ khách hàng chung.", href: "/vi/solutions/founders" },
  { title: "Đội bất động sản", desc: "Luôn hiện diện với mỗi người mua, bán và nhà cung cấp trên mọi niêm yết.", href: "/vi/solutions/real-estate" },
  { title: "Doanh nghiệp", desc: "SSO, SCIM, nhật ký kiểm toán và tính phí theo chỗ ngồi cho tổ chức 500+ người.", href: "/vi/solutions/enterprise" },
];

const STEPS = [
  { title: "Đặt lịch demo", desc: "30 phút với chuyên gia để lập kế hoạch triển khai." },
  { title: "Mời & gắn thương hiệu", desc: "Chúng tôi onboard đội của bạn và khóa mẫu thương hiệu." },
  { title: "Giao thẻ NFC", desc: "Giao hàng hàng loạt đến bàn làm việc hoặc văn phòng, toàn cầu." },
  { title: "Đo lường & mở rộng", desc: "Theo dõi khách hàng & doanh thu ảnh hưởng ngay từ ngày đầu." },
];

const FAQ = [
  { q: "Gói Đội nhóm khác gì gói miễn phí?", a: "Đội nhóm thêm workspace chung, mẫu khóa thương hiệu, mời hàng loạt, SSO, phân quyền theo vai trò, phân tích đội và hỗ trợ ưu tiên. Doanh nghiệp thêm SCIM, nhật ký kiểm toán, DPA tùy chỉnh và CSM riêng." },
  { q: "Chúng tôi có thể giữ CRM hiện tại không?", a: "Có. Banana kết nối với Salesforce, HubSpot, Pipedrive, Attio và các CRM khác qua tích hợp gốc hoặc webhook. Dùng banana làm lớp ghi nhận, CRM của bạn là hệ thống lưu trữ — hoặc chạy banana độc lập." },
  { q: "Bạn có giao thẻ NFC toàn cầu không?", a: "Có. Chúng tôi giao từ trung tâm EU và US đến hơn 100 quốc gia, đặt lại được quản lý tập trung từ dashboard đội." },
  { q: "Điều gì xảy ra khi ai đó rời đội?", a: "Một cú nhấp off-board chỗ ngồi: thẻ ngừng chia sẻ, liên hệ vẫn ở workspace và quyền sở hữu chuyển sang đồng nghiệp. Không mất dữ liệu." },
  { q: "Banana có tuân thủ GDPR & SOC 2 không?", a: "Có — được kiểm toán SOC 2 Type II, tuân thủ GDPR, với tùy chọn lưu trữ chỉ tại EU trên gói doanh nghiệp." },
];

function TeamsPage() {
  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-grid mask-fade-edges" aria-hidden />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-14 pt-16 md:grid-cols-12 md:px-6 md:pt-24">
          <div className="md:col-span-7">
            <p className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              banana.cards cho Đội nhóm
            </p>
            <h1 className="mt-6 max-w-3xl font-display leading-[1.02] tracking-[-0.02em] md:text-[5.25rem] md:leading-[0.98] font-bold text-5xl">
              Một nền tảng. Mọi nhân viên.{" "}
              <em className="italic">Mọi khách hàng được ghi nhận.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Triển khai thẻ kỹ thuật số có thương hiệu, sản phẩm NFC và AI CRM trên toàn đội —
              với SSO, phân tích và kiểm soát quản trị mà IT thực sự chấp nhận.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/vi/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90"
              >
                Đặt lịch demo
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/vi/pricing"
                className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/80"
              >
                Xem giá đội nhóm
              </Link>
            </div>
            <dl className="mt-12 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-6 border-t border-foreground/10 pt-8 md:grid-cols-4">
              {HERO_METRICS.map((m) => (
                <div key={m.l}>
                  <dt className="font-display text-3xl leading-none md:text-4xl">{m.v}</dt>
                  <dd className="mt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {m.l}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="relative md:col-span-5">
            <div className="absolute -inset-8 -z-10 rounded-[2.5rem] bg-foreground/5 blur-3xl" aria-hidden />
            <div className="relative overflow-hidden rounded-[2rem] bg-muted">
              <img src={networking} alt="Một đội đang dùng banana.cards" loading="eager" width={1600} height={2000} className="aspect-[4/5] w-full object-cover" />
            </div>
            <div className="absolute -left-6 top-10 hidden rounded-2xl bg-background p-3 md:flex md:items-center md:gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-lime">
                <Users className="h-4 w-4" />
              </span>
              <div className="pr-1">
                <p className="text-[11px] font-bold leading-none">42 chỗ đã cấp phát</p>
                <p className="mt-1 text-[10.5px] text-muted-foreground">qua Okta SCIM</p>
              </div>
            </div>
            <div className="absolute -right-4 bottom-6 hidden rounded-2xl bg-background p-3 md:block">
              <p className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">Khách hàng ghi nhận Q3</p>
              <p className="mt-1 font-display text-2xl leading-none">3.184</p>
            </div>
          </div>
        </div>

        <div className="bg-muted py-7">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <p className="text-center text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              Được tin dùng bởi đội bán hàng, agency và doanh nghiệp tại hơn 100 quốc gia
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
              {TRUSTED_BY.map((n) => (
                <span key={n} className="text-lg font-bold tracking-tight text-foreground/40">{n}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-12 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Tại sao các đội chọn banana</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Bộ công cụ kết nối mà đội của bạn{" "}
            <em className="italic">thực sự sử dụng.</em>
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-2xl bg-muted p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-6 text-xl font-bold leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <ul className="mt-5 grid gap-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                      <span className="font-medium">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      {DEEP_DIVES.map((d, i) => (
        <section key={d.title} className={i % 2 === 0 ? "bg-muted" : "bg-background"}>
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:px-6">
            <div className={`relative ${d.reverse ? "md:order-2" : ""}`}>
              <div className="absolute -inset-6 rounded-[2rem] bg-foreground/5 blur-3xl" aria-hidden />
              <img src={d.image} alt={d.title} loading="lazy" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
            </div>
            <div className={d.reverse ? "md:order-1" : ""}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{d.eyebrow}</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                {d.title} <em className="italic">{d.italic}</em>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">{d.desc}</p>
              <ul className="mt-6 grid gap-2.5">
                {d.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" /> <span className="font-medium">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-12 flex flex-col items-start gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Mọi thứ đã có</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Một giấy phép. <em className="italic">Toàn bộ nền tảng.</em>
            </h2>
          </div>
          <Link to="/vi/pricing" className="inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline">
            So sánh gói <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PLATFORM_FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl bg-muted p-5">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
                <p className="mt-4 text-sm font-bold">{f.title}</p>
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      <section className="bg-brand-dark text-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 md:grid-cols-12 md:px-6">
          <div className="md:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-lime">Bảo mật & tuân thủ</p>
            <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-5xl">
              Xây cho IT. <em className="italic">Được pháp lý yêu thích.</em>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-background/70">
              banana đi kèm các kiểm soát mà người mua doanh nghiệp mong đợi — và giấy tờ để chứng minh.
            </p>
            <Link
              to="/vi/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground"
            >
              Trao đổi với bộ phận bảo mật <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-3 md:col-span-7 sm:grid-cols-2">
            {SECURITY.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-2xl bg-primary-foreground/[0.06] p-5 backdrop-blur">
                  <Icon className="h-5 w-5 text-brand-lime" strokeWidth={1.75} />
                  <p className="mt-4 text-sm font-bold">{s.title}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-background/65">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Xây cho mọi đội</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Dù đội của bạn gặp gỡ mọi người ra sao, <em className="italic">chúng tôi đều có giải pháp.</em>
          </h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {USE_CASES.map((u) => (
            <Link
              key={u.href}
              to={u.href}
              aria-label={`${u.title} — tìm hiểu thêm về trường hợp sử dụng này`}
              className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70"
            >
              <p className="text-base font-bold">{u.title}</p>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{u.desc}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                Tìm hiểu thêm <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-5xl px-4 py-24 text-center md:px-6">
          <Sparkles className="mx-auto h-6 w-6" />
          <p className="mx-auto mt-6 max-w-3xl font-display text-3xl leading-tight md:text-5xl">
            "Chúng tôi triển khai banana cho 120 nhân viên chỉ trong một tuần. Việc ghi nhận khách hàng tại hội nghị{" "}
            <em className="italic">nhanh hơn 65 lần</em> — và pipeline chưa bao giờ sạch hơn thế."
          </p>
          <p className="mt-8 text-sm font-bold">Priya Sharma</p>
          <p className="text-xs text-muted-foreground">VP Sales, Northbeam</p>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Triển khai trong 4 bước</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Hoạt động trong một tuần, <em className="italic">không phải một quý.</em>
          </h2>
        </div>
        <ol className="mt-12 grid gap-4 md:grid-cols-4">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative rounded-2xl bg-muted p-6">
              <span className="absolute -top-3 left-6 inline-flex h-7 items-center justify-center rounded-full bg-foreground px-3 text-[11px] font-bold text-background">
                Bước {i + 1}
              </span>
              <h3 className="mt-3 text-base font-bold">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </li>
          ))}
        </ol>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-4xl px-4 py-24 md:px-6">
        <div className="mb-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Câu hỏi thường gặp</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Câu hỏi, <em className="italic">đã được giải đáp.</em>
          </h2>
        </div>
        <div className="divide-y divide-foreground/10 rounded-2xl bg-card">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-bold">
                {f.q}
                <ArrowRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-brand-dark px-8 py-16 text-center text-background md:p-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.12),transparent_55%)]" aria-hidden />
          <Headphones className="mx-auto h-6 w-6 text-brand-lime" />
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
            Sẵn sàng triển khai banana cho đội của bạn{" "}
            <em className="italic text-brand-lime">trong tuần này?</em>
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
            Demo 30 phút với chuyên gia. Báo giá tùy chỉnh cho 5 đến 5.000 chỗ ngồi. Không áp lực.
          </p>
          <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/vi/contact" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
              Đặt lịch demo
            </Link>
            <Link to="/vi/pricing" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
              Xem giá đội nhóm
            </Link>
          </div>
        </div>
        </div>
      </section>
    </SiteLayout>
  );
}